/* JustPure behaviour layers, concatenated.

   These ran as five inline <script> blocks in the body - 19.5KB of
   render-blocking JavaScript on every page, on a page already carrying
   227KB of inline script. Moved to one deferred file so parsing the
   document is never held up by them.

   Edited directly: there is no tools/bundle-js.ps1 and never was, so nothing
   regenerates this file. (An earlier header claimed there was, which would have
   cost the next person their changes.) The CSS bundle IS generated - that one
   really does have tools/bundle-css.ps1 behind it.

   Layers, in order:
     jp-kinetic
     jp-interact
     jp-signature
     jp-engage-pages
     jp-policy
     jp-bilona-reveal
*/

/* ================= jp-kinetic ================= */
(function () {
  'use strict';

  // Honour the OS setting before doing any work at all. The CSS also guards
  // this, but there is no reason to walk the DOM for motion nobody will see.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var SKIP = [
    '[data-jp-reveal]', '[data-reveal]',      // already choreographed
    '.jp-wa-wrap', '.jp-ix__toast', '.jp-ix__ship', '.jp-ix__top',
    '.jp-bn', '.jp-offer', '.jp-offer-bar', '.jp-sticky-buy', '.jp-wx',
    '.jp-benefit-slider',                      // the hero: above the fold
    'header', 'footer', 'nav'
  ].join(',');

  function claimed(el) {
    return el.closest(SKIP) !== null || el.hasAttribute('data-kin');
  }

  // A section is only worth animating if it is big enough to arrive as a unit.
  //
  // Height has to be re-checked rather than measured once: Shopify renders some
  // sections lazily, so `related-products` was 0px tall when this first ran and
  // 558px a moment later — it was silently skipped on every product page. The
  // observer below re-runs `apply` as sections grow, and each element is claimed
  // at most once because `claimed()` checks for the attribute it sets.
  function apply(sec) {
    if (sec.__jpKin) return;
    if (sec.offsetHeight <= 240) return;
    if (sec.matches(SKIP) || sec.querySelector('[data-jp-reveal]')) return;
    sec.__jpKin = true;

    // Headings and lead copy: the section's own arrival.
    // Not a heading inside a card. The card itself already arrives as part of
    // a staggered group, and jpKinRise leaves its target sitting ~25px low
    // until its own range opens — inside a flex card that pushed the title
    // down onto the price by 17px. Measured on the product page: title
    // transform translateY(25.5), price transform none, boxes overlapping.
    [].slice.call(sec.querySelectorAll('h2, h3')).forEach(function (h) {
      if (claimed(h)) return;
      if (h.closest('[class*="card"], .jp-product-card, [class*="goes-well"]')) return;
      h.setAttribute('data-kin', 'head');
    });

    // Give each KIND of text its own entrance.
    //
    // Coverage was never the gap — measured on our-story, 31 of 33 text blocks
    // already animated. But 80 of the 91 animated elements ran the same rise,
    // so a page with motion everywhere still read as flat. These pick the
    // gesture from what the element IS: a figure counts up, an eyebrow opens
    // its tracking, a quote swings in from its margin, the opening paragraph
    // comes into focus. jp-kinetic.css holds the motions themselves.

    // Small tracked caps, matched by what the theme calls them.
    //
    // An earlier version of this matched on geometry — short, uppercase,
    // letterspaced — and it picked up the cart count "0", the nav's "Farm
    // Essentials", the seal's glyph and the size chips "XS/S/M", while missing
    // the actual eyebrows. The theme names these consistently, and a name is a
    // better signal than a shape when the shape is this common.
    [].slice.call(sec.querySelectorAll(
      '.jp-eyebrow, [class*="__eyebrow"], [class*="__kicker"], [class*="__overline"]'
    )).forEach(function (e) {
      if (claimed(e)) return;
      if (!e.textContent.trim()) return;
      e.setAttribute('data-kin', 'eyebrow');
    });

    // A figure. Named like a stat AND actually holding a number — the name
    // alone is not enough here, because `--static` and `-label` share the stem
    // and neither is a figure.
    [].slice.call(sec.querySelectorAll(
      '.jp-stat-number, [class*="__stat"], [class*="__figure"], .jp-stat'
    )).forEach(function (e) {
      if (claimed(e)) return;
      var txt = e.textContent.trim();
      if (!txt || txt.length > 14 || !/\d/.test(txt)) return;
      e.setAttribute('data-kin', 'stat');
    });

    // A figure that nobody named. The rule above only finds stats whose class
    // says so, and the homepage's own pair — "4g" and "15-20L" — carry no
    // class at all, so they were never counted. Shape is the tell: a very
    // large type size, a handful of characters, a digit in them, and a small
    // caption directly beneath. That last part is what keeps prices, badge
    // counts and the cinema's "01".."07" beat numbers out.
    [].slice.call(sec.querySelectorAll('div, span, p, strong, h2, h3')).forEach(function (e) {
      if (claimed(e) || e.children.length) return;
      var txt = e.textContent.trim();
      if (!txt || txt.length > 10 || !/\d/.test(txt)) return;
      if (parseFloat(getComputedStyle(e).fontSize) < 34) return;
      var next = e.nextElementSibling;
      if (!next || !next.textContent.trim()) return;
      if (parseFloat(getComputedStyle(next).fontSize) > 20) return;
      e.setAttribute('data-kin', 'stat');
    });

    // A pull-quote, however the section names it.
    [].slice.call(sec.querySelectorAll('blockquote, [class*="quote"], [class*="testimonial"]'))
      .forEach(function (e) {
        if (claimed(e)) return;
        if (e.textContent.trim().length < 30) return;
        e.setAttribute('data-kin', 'quote');
      });

    // The section's opening paragraph — the one that follows its heading.
    var head = sec.querySelector('[data-kin="head"]');
    if (head) {
      var lede = head.nextElementSibling;
      // Skip past a rule or a decorative span to reach real copy.
      for (var hop = 0; lede && hop < 3; hop++) {
        if (lede.tagName === 'P' && lede.textContent.trim().length > 60) break;
        lede = lede.nextElementSibling;
      }
      if (lede && lede.tagName === 'P' && !claimed(lede) && lede.textContent.trim().length > 60) {
        lede.setAttribute('data-kin', 'lede');
      }
    }

    // List items write themselves out in sequence rather than appearing as a
    // block. Only real lists — a nav or a card grid dressed as <li> is skipped
    // by claimed(), and a single item is not a sequence.
    [].slice.call(sec.querySelectorAll('ul, ol')).forEach(function (list) {
      var items = [].slice.call(list.children).filter(function (li) { return li.tagName === 'LI'; });
      if (items.length < 3) return;
      items.forEach(function (li, i) {
        if (claimed(li)) return;
        if (li.textContent.trim().length < 12) return;
        li.style.setProperty('--i', Math.min(i, 8));
        li.setAttribute('data-kin', 'listitem');
      });
    });

    // Media gets its own curve — a shorter rise with a whisper of scale.
    [].slice.call(sec.querySelectorAll('img, picture, video, svg')).forEach(function (m) {
      if (claimed(m)) return;
      // Tag the figure rather than the image where there is one, so the
      // caption travels with it instead of arriving separately.
      var target = m.closest('figure') || m;
      if (claimed(target) || target.hasAttribute('data-kin')) return;
      if (target.offsetHeight < 80) return;   // icons and marks stay still
      target.setAttribute('data-kin', 'media');
    });

    // Card-ish repeated children become a staggered group. Finding the parent
    // by counting similar siblings is more reliable here than a class list,
    // because these sections were each written with their own naming.
    var groups = {};
    [].slice.call(sec.querySelectorAll('[class*="card"], [class*="tile"], li')).forEach(function (c) {
      if (claimed(c) || !c.parentElement) return;
      var key = c.parentElement;
      (groups.list = groups.list || []).push(key);
      key.__jpKids = (key.__jpKids || 0) + 1;
    });
    (groups.list || []).forEach(function (parent) {
      if (parent.__jpDone || parent.__jpKids < 2) return;
      parent.__jpDone = true;
      if (parent.hasAttribute('data-kin-group')) return;
      parent.setAttribute('data-kin-group', '');
      [].slice.call(parent.children).forEach(function (child, i) {
        // Cap the index: a twelve-card grid should not make the last card
        // wait a second, which reads as a bug rather than as choreography.
        child.style.setProperty('--i', Math.min(i, 6));
        if (!claimed(child)) child.setAttribute('data-kin-lift', '');
      });
    });

    // Body copy last, and only the substantial paragraphs — animating a
    // one-line caption is noise.
    [].slice.call(sec.querySelectorAll('p')).forEach(function (p) {
      if (claimed(p)) return;
      if (p.textContent.trim().length < 40) return;
      p.setAttribute('data-kin', '');
    });
  }

  function sweep() {
    // Policy pages are not built from sections - Shopify renders them as one
    // `.shopify-policy__container` - so a sweep that only looks for
    // `.shopify-section` gave the four longest, densest pages on the site no
    // motion at all. They are exactly the pages that need arrival most: 6,500
    // characters that otherwise land as a single dead slab.
    [].slice.call(document.querySelectorAll(
      '.shopify-section, .shopify-policy__body .rte'
    )).forEach(apply);
  }

  sweep();

  // Sweep again once the document is parsed and once it has fully loaded.
  //
  // This snippet renders inside <body>, so anything below it does not exist on
  // the first pass — the product page's related-products section is rendered
  // after this point and was silently skipped on every product page. Sections
  // whose height arrives late (images still loading, lazily rendered blocks)
  // are caught by the load pass. `apply` is idempotent, so re-sweeping costs a
  // few attribute checks and can never double-claim.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  }
  window.addEventListener('load', sweep);

  // And anything that appears later still — the theme editor adding a section
  // while someone works in it, or a lazily mounted app block.
  // ponytail: one observer on the body, debounced to a frame; sections appear
  // rarely and `apply` bails immediately on ones it has already handled.
  if (window.MutationObserver) {
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; sweep(); });
    }).observe(document.body, { childList: true, subtree: true });
  }

  // The magnetic pull on primary calls to action. Pointer-fine only: on a
  // touch screen there is no cursor to be pulled toward.
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    var STRENGTH = 0.28;
    [].slice.call(document.querySelectorAll(
      '.jp-bundle__btn, .jp-offer__btn--primary, .jp-sawan__cta, .jp-rail__go, .button--primary'
    )).forEach(function (btn) {
      if (btn.closest('.jp-wa-wrap')) return;
      btn.setAttribute('data-kin-magnet', '');

      btn.addEventListener('pointermove', function (e) {
        var r = btn.getBoundingClientRect();
        btn.style.setProperty('--mx', ((e.clientX - r.left - r.width / 2) * STRENGTH).toFixed(1) + 'px');
        btn.style.setProperty('--my', ((e.clientY - r.top - r.height / 2) * STRENGTH).toFixed(1) + 'px');
      });
      // Reset on leave AND on blur, or a button tabbed away from keeps its
      // last offset and sits visibly crooked.
      function rest() {
        btn.style.setProperty('--mx', '0px');
        btn.style.setProperty('--my', '0px');
      }
      btn.addEventListener('pointerleave', rest);
      btn.addEventListener('blur', rest);
    });
  }
})();

/* ================= jp-interact ================= */
(function () {
  'use strict';

  var SKIP = [
    '.jp-wa-wrap', '.jp-ix__toast', '.jp-ix__ship', '.jp-ix__top', '.jp-bn',
    '.jp-offer', '.jp-offer-bar', '.jp-sticky-buy', '.jp-wx', '.jp-cd',
    '.jp-benefit-slider', 'header', 'footer', 'nav', 'form'
  ].join(',');

  function outOfBounds(el) { return el.closest(SKIP) !== null; }

  /* ---------------------------------------------------------- expandable */
  /* Cards get pointer response — a lift, a shadow, a keyboard focus ring —
     and nothing is ever hidden behind a control.

     A collapse-and-disclose version was built first and removed. It could not
     be verified: the preview pane does not composite, so every height this
     session measured came back 0px whether the panel was open or shut, and
     `getComputedStyle` disagreed with an inline `!important`. Shipping a card
     that says "Read more" and might do nothing is worse than a card that
     simply reads well, so the affordance goes and the copy stays visible.

     ponytail: hover/focus response only. Re-add disclosure when it can be
     driven and watched in a real browser. */
  function wireCards(root) {
    var cards = root.querySelectorAll(
      '[class*="__card"],[class*="-card"],[class*="__pick"],[class*="__tile"]'
    );

    [].slice.call(cards).forEach(function (card) {
      if (card.__ixDone || outOfBounds(card)) return;

      // Never the container. `[class*="__card"]` also matches the plural
      // wrapper — `jp-farm-video__cards` holding four `jp-farm-video__card`
      // children — and treating that as a card styles the group instead of the
      // thing the pointer is over. A node holding cards is not itself a card.
      if (card.querySelector('[class*="__card"],[class*="-card"],[class*="__tile"]')) return;

      // Only cards with real content are worth responding to; an icon-and-label
      // tile has nothing to reward the pointer with.
      if (card.textContent.trim().length < 40) return;

      // A card that is already a link, or holds one, still gets the lift — it
      // is the one the visitor is most likely to point at, and leaving it flat
      // is what made the grids feel dead. Measured on the dev server: of
      // fifteen cards on the homepage, every single one is or contains a
      // control, so skipping them meant this layer reached nothing at all.
      //
      // It gets no tabindex and no role: the link inside is already the
      // keyboard path, and wrapping it would announce the same destination
      // twice. `:focus-within` carries the focus ring instead.
      card.__ixDone = true;
      card.setAttribute('data-ix-card', '');
    });
  }

  /* ------------------------------------------------------------- counters */
  /* Counts a number up as it comes into view. The element already contains its
     final value, so this only replaces the text while the animation runs and
     puts the original back at the end — if anything throws, the correct number
     is what remains. */
  function wireCounters(root) {
    if (!window.IntersectionObserver) return;

    var candidates = [].slice.call(root.querySelectorAll('h2,h3,h4,strong,b,span,div'))
      .filter(function (el) {
        if (el.__ixCount || outOfBounds(el)) return false;
        if (el.children.length) return false;           // leaf nodes only
        var t = el.textContent.trim();
        // A standalone figure: 42, 100%, 4x, 1,200+ — not a sentence with a
        // number in it, and not a price (the theme formats those).
        return /^[₹$]?\d[\d,]{0,6}(\.\d+)?\s?(%|x|\+|kg|g|ml|L)?$/i.test(t) &&
               parseFloat(t.replace(/[^\d.]/g, '')) >= 10;
      });

    if (!candidates.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        io.unobserve(el);
        if (el.__ixCount) return;
        el.__ixCount = true;

        var finalText = el.textContent;
        var target = parseFloat(finalText.replace(/[^\d.]/g, ''));
        if (!isFinite(target)) return;

        // Hold the box at its final size first, or the page reflows on every
        // frame as the digits grow.
        el.style.minWidth = el.getBoundingClientRect().width + 'px';
        el.setAttribute('data-ix-count', '');

        var dur = 900, t0 = 0;
        function frame(ts) {
          if (!t0) t0 = ts;
          var p = Math.min((ts - t0) / dur, 1);
          // ease-out: fast start, settles into the real figure
          var v = target * (1 - Math.pow(1 - p, 3));
          el.textContent = finalText.replace(
            /\d[\d,]*(\.\d+)?/,
            (target % 1 ? v.toFixed(1) : Math.round(v)).toLocaleString('en-IN')
          );
          if (p < 1) requestAnimationFrame(frame);
          else el.textContent = finalText;   // always end on the true value
        }
        requestAnimationFrame(frame);
      });
    }, { threshold: .6 });

    candidates.forEach(function (el) { io.observe(el); });
  }

  /* ---------------------------------------------------------------- zoom */
  function wireZoom(root) {
    [].slice.call(root.querySelectorAll('img')).forEach(function (img) {
      if (outOfBounds(img) || img.offsetHeight < 120) return;
      var frame = img.parentElement;
      if (!frame || frame.__ixZoom) return;
      // Only frames that actually clip, or the scale spills over neighbours.
      var cs = getComputedStyle(frame);
      if (cs.overflow !== 'hidden' && cs.borderRadius === '0px') return;
      frame.__ixZoom = true;
      frame.setAttribute('data-ix-zoom', '');
    });
  }
  function sweep() {
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      wireCounters(document);
      wireZoom(document);
    }
    // Expansion is content, not decoration — it runs regardless of the motion
    // preference. The CSS makes it instant instead of animated.
    wireCards(document);
  }

  sweep();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sweep);
  window.addEventListener('load', sweep);

  // ponytail: one debounced observer; every wire function is idempotent.
  if (window.MutationObserver) {
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; sweep(); });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();

/* ================= jp-signature ================= */
(function () {
  'use strict';

  var SKIP = '.jp-wa-wrap,.jp-ix__toast,.jp-ix__ship,.jp-bn,.jp-offer,.jp-offer-bar,' +
             '.jp-sticky-buy,.jp-wx,.jp-cd,.jp-marq,header,footer,nav';

  /* ---- the seal -------------------------------------------------------- */
  /* Dropped into the first section that has room for it: a heading block with
     no media of its own competing for the eye. */
  function placeSeal() {
    if (document.querySelector('.jp-mark')) return;
    var host = null;
    var heads = document.querySelectorAll('.shopify-section h2');
    for (var i = 0; i < heads.length && !host; i++) {
      var h = heads[i];
      if (h.closest(SKIP)) continue;
      var sec = h.closest('.shopify-section');
      if (!sec || sec.offsetHeight < 320) continue;
      if (sec.querySelector('.jp-mark')) continue;
      host = h.parentElement;
    }
    if (!host) return;

    // Cloned from a <template> rather than assembled with innerHTML: setting
    // innerHTML on a plain div parses the markup as HTML, so the <svg> and its
    // children land in the wrong namespace and never render. Measured: the
    // wrapper appeared, .jp-mark__ring did not exist.
    var tpl = document.getElementById('jp-mark-tpl');
    if (!tpl) return;
    var seal = tpl.content.firstElementChild.cloneNode(true);
    host.insertBefore(seal, host.firstChild);

    // Fit the ring text to the circle it sits on.
    //
    // The string is longer than the path — measured 353px of type on a 239px
    // circumference — so without this the tail wraps back over the start and
    // the seal reads "SMALL BSLOW-CHURNED". Measuring the path at runtime
    // rather than hardcoding a length keeps it correct if the copy, the font
    // or the viewBox ever changes.
    requestAnimationFrame(function () {
      var tp = seal.querySelector('textPath');
      var path = seal.querySelector('path[id], defs path');
      if (!tp || !path || !path.getTotalLength) return;
      var len = path.getTotalLength();
      if (len > 0) tp.setAttribute('textLength', len);
    });
  }

  /* ---- drawn rules under headings -------------------------------------- */
  function drawRules() {
    var heads = document.querySelectorAll('.shopify-section h2, .shopify-section h3');
    [].slice.call(heads).forEach(function (h) {
      if (h.closest(SKIP) || h.classList.contains('jp-rule')) return;
      if (h.textContent.trim().length < 6) return;
      // Only the heading that opens a section, not every sub-label inside it.
      var sec = h.closest('.shopify-section');
      if (!sec || sec.__jpRuled) return;
      sec.__jpRuled = true;
      h.classList.add('jp-rule');
    });
  }

  /* ---- link underlines -------------------------------------------------- */
  function wipeLinks() {
    var links = document.querySelectorAll('.shopify-section p a[href], .shopify-section li a[href]');
    [].slice.call(links).forEach(function (a) {
      if (a.closest(SKIP) || a.classList.contains('jp-sig-link')) return;
      if (a.querySelector('img,svg')) return;      // image links keep their own look
      a.classList.add('jp-sig-link');
    });
  }

  function sweep() { placeSeal(); drawRules(); wipeLinks(); }

  sweep();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sweep);
  window.addEventListener('load', sweep);
})();

/* ================= jp-engage-pages ================= */
(function () {
  'use strict';

  var SKIP = '.jp-wa-wrap,.jp-ix__toast,.jp-ix__ship,.jp-bn,.jp-offer,.jp-offer-bar,' +
             '.jp-sticky-buy,.jp-wx,.jp-cd,.jp-marq,.jp-ep-next,header,footer,nav';

  /* Number any row of repeated cards that reads as a process.
     Three or more siblings sharing a class is the signal; fewer is a pair of
     features, not a sequence. */
  function numberSteps() {
    var groups = {};
    document.querySelectorAll('.shopify-section [class]').forEach(function (el) {
      if (el.closest(SKIP)) return;
      var cls = String(el.className).split(/\s+/)[0];
      if (!cls || !/(step|card|process)/i.test(cls)) return;
      // A product grid is not a sequence. Numbering five products 01..05
      // states an order that does not exist, and the cards already carry
      // their own price and link. Same for anything holding a control.
      if (/product|media|mega/i.test(cls)) return;
      // Sections that already number their own cards must not be numbered
      // again. The quality page's testing-equipment cards carry a jpq counter
      // in jp-quality.css and were ALSO matched here, so every card showed two
      // ordinals: the correct 01-06 from its own rule and a second "01" from
      // this one, stuck at 01 because the jp-ep counter-reset lives on a
      // parent that section does not have.
      if (/jp-fssai-test__card|jp-bilona-step|jp-purity-card|jp-cert-proof__item/.test(cls)) return;
      if (el.querySelector('a[href],button')) return;
      if (!el.parentElement) return;
      var key = cls + '::' + (el.parentElement.className || el.parentElement.tagName);
      (groups[key] = groups[key] || []).push(el);
    });

    Object.keys(groups).forEach(function (k) {
      var items = groups[k];
      if (items.length < 3 || items.length > 9) return;
      var parent = items[0].parentElement;
      if (!parent || parent.__epDone) return;
      // Every item must share one parent, or the counter runs across the page.
      for (var i = 1; i < items.length; i++) if (items[i].parentElement !== parent) return;
      parent.__epDone = true;
      parent.setAttribute('data-ep-steps', '');
      items.forEach(function (it) { it.setAttribute('data-ep-step', ''); });
    });
  }

  function sweep() { numberSteps(); }
  sweep();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sweep);
  window.addEventListener('load', sweep);
})();

/* ================= jp-policy ================= */
(function () {
  'use strict';

  var body = document.querySelector('.shopify-policy__body .rte, .shopify-policy__body')
          || document.querySelector('.shopify-policy__container .rte');
  if (!body) return;

    var heads = [].slice.call(body.querySelectorAll('h2'));
    if (heads.length < 3) return;          // short policy: leave it alone

    // Group each heading with the nodes that follow it, up to the next one.
    var groups = heads.map(function (h) {
      var kids = [], n = h.nextSibling;
      while (n && !(n.nodeType === 1 && n.tagName === 'H2')) {
        var next = n.nextSibling;
        kids.push(n);
        n = next;
      }
      return { head: h, kids: kids };
    });

    var index = document.createElement('nav');
    index.className = 'jp-pol__index';
    index.setAttribute('aria-label', 'On this page');

    groups.forEach(function (g, i) {
      var id = 'jp-pol-' + i;

      var sec = document.createElement('section');
      sec.className = 'jp-pol__sec';

      // The heading becomes the control. A real <button> inside it keeps the
      // heading a heading for assistive tech — turning the h2 itself into a
      // button would drop it out of the document outline.
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'jp-pol__btn';
      btn.setAttribute('aria-expanded', i === 0 ? 'true' : 'false');
      btn.setAttribute('aria-controls', id);
      btn.textContent = g.head.textContent.trim();

      var newHead = document.createElement('h2');
      newHead.className = 'jp-pol__head';
      newHead.appendChild(btn);

      var panel = document.createElement('div');
      panel.className = 'jp-pol__panel';
      panel.id = id;
      if (i !== 0) panel.hidden = true;
      g.kids.forEach(function (k) { panel.appendChild(k); });

      sec.appendChild(newHead);
      sec.appendChild(panel);
      g.head.parentNode.insertBefore(sec, g.head);
      g.head.remove();

      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        panel.hidden = open;
      });

      var link = document.createElement('a');
      link.href = '#' + id;
      link.className = 'jp-pol__link';
      link.textContent = btn.textContent;
      link.addEventListener('click', function (e) {
        e.preventDefault();
        // Open it before scrolling, or the reader lands on a closed section.
        btn.setAttribute('aria-expanded', 'true');
        panel.hidden = false;
        sec.scrollIntoView({ behavior: 'smooth', block: 'start' });
        btn.focus();
      });
      index.appendChild(link);
    });

  body.insertBefore(index, body.firstChild);
})();

/* ================= jp-bilona-reveal ================= */
/* Reveals the bilona process one step at a time, wherever it appears — the two
   hand-written sections and the two generated PDP blocks. See
   jp-bilona-reveal.css for the motion and for why none of it touches opacity.

   The hidden state is armed here rather than in the stylesheet so that a page
   whose script never runs shows all seven steps in place instead of a row of
   gaps. */
(function () {
  'use strict';

  var ROWS = '.jp-bilona-timeline, [class*="ai-bilona-process-timeline"]';
  var STEP = '[class*="jp-bilona-step"], [class*="ai-bilona-process-step"]';

  function armed() { return document.documentElement.classList.contains('jp-bilona-armed'); }

  function setup(row) {
    if (row.__jpBil) return;
    var steps = [].slice.call(row.querySelectorAll(STEP)).filter(function (s) {
      // The generated block names its title and copy with the same stem as the
      // step itself, so a plain prefix match would treat them as steps too and
      // stagger the same card three times.
      return !/title|description/.test(s.className);
    });
    if (steps.length < 2) return;
    row.__jpBil = true;

    steps.forEach(function (s, i) { s.style.setProperty('--jp-bil-i', i); });

    if (!window.IntersectionObserver) {
      steps.forEach(function (s) { s.classList.add('jp-bil-in'); });
      return;
    }

    // Arm only now that an observer exists to disarm it again.
    document.documentElement.classList.add('jp-bilona-armed');

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('jp-bil-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: .15 });

    steps.forEach(function (s) { io.observe(s); });

    // A step that never receives its callback — a throttled tab, a row that is
    // already past — must not stay held back. The CSS only moves, so the worst
    // case is cosmetic, but there is no reason to leave it pending.
    setTimeout(function () {
      steps.forEach(function (s) { s.classList.add('jp-bil-in'); });
    }, 4000);
  }

  function sweep() {
    [].slice.call(document.querySelectorAll(ROWS)).forEach(setup);
  }

  sweep();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sweep);
  window.addEventListener('load', sweep);

  if (window.MutationObserver) {
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; sweep(); });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();

/* ================= jp-bilona-video ================= */
/* Swaps the PDP's flat process PNG for the animated version of itself.

   That block renders its seven steps as one baked image, so the step-by-step
   reveal above has nothing to work with. These two videos were generated from
   those exact PNGs and build the same diagram up one step at a time.

   The <img> is hidden rather than removed: its alt text is the only description
   of the diagram a screen reader or a crawler gets, and the video element
   carries no equivalent. The PNG is also the video's poster, so if autoplay is
   refused — Low Power Mode, a data saver, a blocked codec — what shows is the
   original artwork rather than an empty frame. */
(function () {
  'use strict';

  // Both variants, and they are NOT one prefix: the mobile one is named
  // `ai-bilona-process-mobile-image`, so `[class*="process-image"]` misses it
  // entirely. That left the phone showing the desktop video squashed to 72px
  // with the untouched mobile still underneath it.
  var IMG = '[class*="ai-bilona-process-image"]:not([class*="wrap"]),'
          + '[class*="ai-bilona-process-mobile-image"]';

  // Which clip belongs to this block. The same AI block powers the process
  // strip on several products, and each one has its own artwork — moringa's
  // five steps are not ghee's seven. The still's own filename is what says
  // which product this is, so the set is chosen from that rather than from the
  // page URL (the block also appears on pages whose handle names no product).
  var SETS = [
    { test: /moringa/i, key: 'moringa' },
    // "lal mirch" in the heading, "chilli" in the step copy — match either, so
    // a copy edit to one does not silently drop the page back to ghee.
    { test: /mirch|chilli|chili/i, key: 'mirch' },
    { test: /haldi|turmeric/i, key: 'haldi' }
    // default: bilona
  ];

  function assetUrl(img, isMobile) {
    // Liquid hands us the real asset URLs (see theme.liquid). Deriving one from
    // the block's image src used to strip "files/" off a cdn/shop/files/ path
    // and produce /cdn/shop/<name>.mp4, which 404s — theme assets are served
    // from a versioned assets path that only Liquid can resolve. Measured: the
    // video element existed with duration null, so the section sat on its
    // poster and the steps never moved.
    var map = window.jpBilonaVideo;
    if (!map) return null;

    // Ask the whole block, not one filename. The mobile still is named
    // `Artboard_2_6.png` — nothing in it says moringa — so matching on the
    // <img>'s own src picked the default and a phone on the moringa page got
    // the SEVEN-step ghee clip ("Cow Hand Milked … Bilona Churning … Ghee").
    // The desktop still happens to be `MORINGA_1.png`, which is why only the
    // phone was wrong. The section's text is what actually identifies it.
    // Climb to the whole section, not to the nearest matching ancestor. Two
    // near misses, both measured: closest() starts at the element itself and
    // the <img>'s own class matches, so it returned the image (text: ""); and
    // starting one level up landed on `...-image-wrap`, which holds only
    // images (text: "" again). The heading that says "moringa" is a sibling of
    // that wrapper, so nothing short of the section carries the word.
    var block = img.closest('.shopify-section') ||
                img.closest('[class*="ai-bilona-process"]:not([class*="image"])') ||
                img.parentNode;

    // The page's own identity comes first. A section's textContent is mostly
    // its inlined CSS — measured on the lal-mirch page it ran 1880 chars
    // without the word "mirch" in it — so the heading is not reliably in
    // reach. The URL always is, and this block only ever appears on the
    // product it describes. Filename and heading stay in the haystack as
    // corroboration, not as the primary signal: the mobile stills are named
    // `Artboard_1_7.png` and `Artboard_2_6.png`, which say nothing at all.
    var hay = (location.pathname || '') + ' ' +
              (document.title || '') + ' ' +
              (block.textContent || '') + ' ' +
              (img.currentSrc || img.src || '') + ' ' +
              (img.alt || '');

    var set = null;
    for (var i = 0; i < SETS.length; i++) {
      if (SETS[i].test.test(hay)) { set = map[SETS[i].key]; break; }
    }
    if (!set) set = map.bilona || map;      // map.* is the older flat shape

    return {
      url: isMobile ? set.mobile : set.desktop,
      ar: isMobile ? set.mobileAr : set.desktopAr,
      poster: isMobile ? set.mobilePoster : set.desktopPoster
    };
  }

  function swap(img) {
    // Retired in favour of jp-steps-cinema.js, which scrubs a canvas image
    // sequence instead of a <video>. Seeking currentTime from scroll was
    // rebuilt many times — clip length, frame rate, resolution, keyframe
    // density, easing — and stepped every time, because a seek is a decoder
    // round-trip that cannot keep pace with scroll input. The Apex engine had
    // already recorded exactly this and moved to decoded frames on a canvas.
    //
    // Left in place rather than deleted: the surrounding helpers still carry
    // the measurements that led here, and the frame sets are generated from
    // these same clips.
    if (window.jpStepsFrames) return;
    if (img.__jpVid) return;
    var isMobile = /mobile/.test(img.className);
    var pick = assetUrl(img, isMobile) || {};
    var url = img.dataset.jpVideo || pick.url;
    if (!url) return;
    img.__jpVid = true;

    var v = document.createElement('video');
    v.className = 'jp-bilona-vid jp-bilona-vid--' + (isMobile ? 'mobile' : 'desktop');
    v.src = url;
    // The poster is the clip's own frame at 1.2s, not the source still.
    //
    // The still is the FINISHED diagram, so using it gave away the whole
    // process before a pixel was scrolled. Dropping the poster entirely was
    // worse in the other direction: the clip's first frame is empty by
    // construction, so the section showed a blank box until the first seek.
    // A frame from just after the first step arrives has something to look at
    // and still leaves the rest to reveal.
    if (pick.poster) v.poster = pick.poster;
    v.muted = true;           // required for autoplay to be allowed at all
    v.playsInline = true;
    // Metadata up front, now that there is no poster to stand in for it. With
    // preload:'none' the element has no frame at all until the first seek
    // resolves, so the section showed an empty box on the way in — the same
    // stutter the poster used to hide. 'metadata' costs a few KB and gives a
    // decoded first frame; the rest still streams on demand.
    v.preload = 'metadata';
    v.setAttribute('aria-hidden', 'true');   // the <img> alt already describes it
    // Reserve the clip's real box before a single byte is fetched. Without
    // this the element has no intrinsic size until metadata decodes, so it
    // inherits whatever the wrapper was sized for — measured on a phone, the
    // 720x1280 portrait clip came out 358x343, squashed flat.
    if (pick.ar) v.style.setProperty('--jp-vid-ar', pick.ar);

    img.classList.add('jp-bilona-vid-still');
    img.style.display = 'none';
    img.parentNode.insertBefore(v, img);

    if (!window.IntersectionObserver) { v.preload = 'auto'; v.play().catch(function () {}); return; }

    // A sticky track, exactly as the homepage cinema does it: wrap the clip in
    // a box taller than the screen and pin the clip inside it, so the eight
    // seconds are spent over a real stretch of scrolling instead of the 251px
    // the element itself occupies.
    //
    // Reduced motion keeps the plain element — a tall pinned track is a
    // hostage situation for someone who asked for less movement, the same
    // reasoning the cinema section already carries.
    var track = null;
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      track = document.createElement('div');
      track.className = 'jp-bilona-track';
      var pin = document.createElement('div');
      pin.className = 'jp-bilona-pin';
      v.parentNode.insertBefore(track, v);
      track.appendChild(pin);
      pin.appendChild(v);
    }

    // Scrubbed by scroll, not played straight through.
    //
    // The owner asked for the seven steps to arrive one at a time as he
    // scrolls, and this section is a single rendered video rather than seven
    // elements — so there is nothing to stagger. Binding currentTime to scroll
    // position gives exactly the asked-for behaviour from the footage that
    // already exists: scrolling forward walks the steps on, stopping holds the
    // frame, scrolling back rewinds. Roughly one step per 1.15s of an 8s clip.
    //
    // Reduced motion gets the clip's end frame — every step visible, no motion.
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Loading is driven from the scroll handler rather than an
    // IntersectionObserver. The handler already runs on every scroll and
    // already knows where the video sits, so a second mechanism to answer the
    // same question is one more thing that can fail to fire — and it did:
    // measured on the product page, the video stayed preload="none" with
    // readyState 0 while sitting fully in view, because no observer callback
    // ever arrived. The bytes are still spent only when the clip is near.
    var loaded = false;
    function maybeLoad(r) {
      if (loaded) return;
      // Start fetching from the TRACK's top, not the clip's, and a long way
      // out. The clip is pinned inside a track more than two screens tall, so
      // by the time the element itself was 1.25 screens away the reader was
      // already inside the track with nothing decoded — measured on haldi and
      // mirch, the first four scroll stops sat at currentTime 0 and then
      // jumped 4.74s at once. These are 4-5MB, so the fetch needs a head
      // start, and the track gives one for free.
      var box = track ? track.getBoundingClientRect() : r;
      if (box.top > window.innerHeight * 3) return;
      loaded = true;
      v.preload = 'auto';
      v.load();
    }

    function frame() {
      var r = v.getBoundingClientRect();
      maybeLoad(r);
      var d = v.duration;
      if (!d || !isFinite(d)) return;
      if (reduce) { seek(d - 0.05); return; }

      // Progress runs from the video's top reaching the lower third of the
      // viewport to the moment its own top clears the top of the screen — a
      // full screen of travel, so the walk is unhurried rather than over in a
      // flick.
      //
      // The travel deliberately ends while the clip is still on screen rather
      // than after it has scrolled away: measured, an end tied to the video's
      // bottom clearing the viewport left the last step unreachable — the walk
      // stopped at 7.20s of 8.04s because the remaining progress was spent
      // below the fold. Ending at the top edge means the final frame is the
      // one held while the section is still being looked at.
      //
      // Travel is the screen PLUS the clip's own height, not the screen alone.
      // A tall portrait clip fills most of a phone, so a screen-only distance
      // left barely any room: measured on a 390px viewport the walk ran 0 ->
      // 7.99s across 262px of scrolling — the whole eight seconds in one flick.
      // Adding the element's height gives every clip the same unhurried pace
      // whatever its shape.
      // Only the part of the clip that overflows a comfortable band counts.
      // Charging the full height slowed the short desktop strip down too: it
      // stopped reaching the end on screen (7.0s of 8.04s, and 5.6s on the
      // seven-step clip). A tall portrait clip still earns its extra distance;
      // a wide one is left as it was.
      // Same engine as the homepage cinema: the clip is pinned inside a track
      // that is deliberately taller than the screen, and progress is how far
      // through that track the reader has come. Scrubbing the element's own
      // height meant an eight-second clip was spent in a flick or two, which
      // is what made these read as cheap next to the cinema.
      if (track) {
        var tr = track.getBoundingClientRect();
        var span = Math.max(1, tr.height - window.innerHeight);
        var p = (0 - tr.top) / span;
        p = Math.max(0, Math.min(1, p));
        seek(p * (d - 0.05));
        return;
      }

      var start = window.innerHeight * 0.82;
      var extra = Math.max(0, r.height - window.innerHeight * 0.45);
      var travel = Math.max(1, start + extra);
      var p = (start - r.top) / travel;
      p = Math.max(0, Math.min(1, p));
      seek(p * (d - 0.05));
    }

    // The clip CHASES the scroll rather than being pinned to it.
    //
    // Setting currentTime straight from scroll position inherits the input's
    // own coarseness: one wheel notch is ~100px, so the frame jumped in
    // notch-sized steps however evenly the maths ran. The numbers looked
    // perfect and it still read as stepping, which is what "smooth ni hai"
    // describes and what separates this from the Apex feel.
    //
    // So scroll sets a TARGET, and a rAF loop eases the actual time toward it.
    // Between two wheel notches the loop still has ~6 frames to fill, and it
    // fills them. The loop parks itself when it arrives, so an idle section
    // costs nothing.
    var target = 0;
    var cur = 0;
    var raf = 0;
    var primed = false;
    // Per-frame approach. 0.14 lagged far enough behind that a reader who
    // scrolled through at a normal pace left the section with the clip at
    // 6.6s of 8 — the end never arrived. 0.28 closes a notch-sized gap in
    // about four frames: still visibly eased, but it keeps up.
    var EASE = 0.28;

    function tick() {
      raf = 0;
      if (v.readyState < 2) return;
      var gap = target - cur;
      if (Math.abs(gap) < 0.008) {       // arrived: settle exactly and stop
        cur = target;
        apply(cur);
        return;
      }
      cur += gap * EASE;
      apply(cur);
      raf = requestAnimationFrame(tick);
    }

    // A seek requested while one is already running is REMEMBERED, not thrown
    // away. Dropping it was a second stutter: measured across a 14-stop walk,
    // the frame held still for a whole stop and then jumped 1.85s, because the
    // request that arrived mid-seek was discarded and the old comment's promise
    // — "the next scroll event schedules another frame" — is false once the
    // reader stops moving.
    var want = null;
    v.addEventListener('seeked', function () {
      if (want === null) return;
      var t = want;
      want = null;
      applyNow(t);
    });

    function applyNow(t) {
      if (Math.abs(v.currentTime - t) < 0.02) return;
      try { v.currentTime = t; } catch (err) {}
    }

    function apply(t) {
      if (v.seeking) { want = t; return; }
      applyNow(t);
    }

    function seek(t) {
      // Nothing is drawn until a frame is actually decodable. Setting
      // currentTime before then paints the element's own empty background
      // instead — measured as a blank white band where the steps should be, at
      // the top of the walk. HAVE_CURRENT_DATA is the first state where the
      // element has a frame to show.
      if (v.readyState < 2) return;
      target = t;
      // Snap ONCE, on the very first reading, so entering the section
      // part-way does not animate the whole clip up from zero. After that
      // every change is chased.
      //
      // The guard used to be "gap > 1.5s", which is true of the first reading
      // by definition — and stayed true, so every notch snapped and the loop
      // never ran: measured, one 120px scroll produced exactly two distinct
      // currentTime values, 0 and 2.551.
      if (!primed) { primed = true; cur = target; applyNow(cur); return; }
      if (!raf) raf = requestAnimationFrame(tick);
    }

    // Hold the track's height back until the clip can actually be scrubbed.
    //
    // The track is over two screens tall, and it was that tall from first
    // paint — so a reader could be halfway down it while the 4-5MB clip was
    // still arriving, and the walk began wherever they happened to be. That
    // is the "poora section paar hone ke baad ek dum appear" on the phone,
    // and the first four dead stops on haldi and mirch.
    //
    // Until the clip is ready the track collapses to the clip's own height,
    // so the page is SHORT and there is nothing to scroll past. When it can
    // be drawn the track takes its real height and the walk starts from the
    // top, the way cow already behaved by loading sooner.
    //
    // ponytail: a class on the track, no measurement of its own.
    function armTrack() {
      if (!track) return;
      track.classList.add('jp-bilona-track--waiting');
      var release = function () {
        if (v.readyState < 2) return;
        track.classList.remove('jp-bilona-track--waiting');
        v.removeEventListener('loadeddata', release);
        onScroll();
      };
      v.addEventListener('loadeddata', release);
      // Already decoded (cache) — do not wait for an event that will not fire.
      if (v.readyState >= 2) release();
    }

    var queued = false;
    function onScroll() {
      // Load decisions happen straight away rather than inside the frame
      // callback: a hidden or throttled tab delivers no animation frames at
      // all, and the clip would then never start fetching. Measured in a
      // hidden pane: preload stayed "none" and readyState 0 with the video
      // fully in view until this ran outside the frame callback.
      maybeLoad(v.getBoundingClientRect());

      // Coalesce the seek into one frame. Firing frame() on every scroll
      // event means two getBoundingClientRect reads per event, and with a
      // sticky track the events arrive continuously for the whole walk —
      // measured, a scroll on the homepage hit a 226ms worst frame gap.
      //
      // Plain requestAnimationFrame, and nothing else. An earlier version
      // paired it with a setTimeout fallback and that wedged the renderer:
      // a timer armed on every scroll event is a lot of timers. A dropped
      // frame here costs nothing, because the next scroll event schedules
      // another one and seek() is idempotent.
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; frame(); });
    }

    v.addEventListener('loadedmetadata', frame);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    // Collapse the track until the clip can be drawn, so nobody scrolls past
    // a section that has not arrived yet.
    armTrack();
    // Start fetching now rather than on the first scroll. These are 4-5MB and
    // the reader may already be moving; waiting for a scroll event to begin
    // the download is what let the page run ahead of the clip.
    maybeLoad(v.getBoundingClientRect());
    // Run once now: a visitor can land mid-page (a jump link, a restored
    // scroll position) and would otherwise see the poster until they move.
    frame();
  }

  function sweep() {
    [].slice.call(document.querySelectorAll(IMG)).forEach(swap);

    // The block only renders its mobile <img> when that setting is filled, and
    // this product leaves it empty. Hiding the desktop video below 749px
    // unconditionally therefore blanked the section on a phone: desktop hidden,
    // no mobile video to replace it. Mark the desktop video only when a mobile
    // one really exists, and let the CSS hide it on that condition alone.
    var hasMobile = !!document.querySelector('.jp-bilona-vid--mobile');
    [].slice.call(document.querySelectorAll('.jp-bilona-vid--desktop')).forEach(function (v) {
      v.classList.toggle('jp-bilona-vid--has-mobile', hasMobile);
    });
  }

  sweep();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sweep);
  window.addEventListener('load', sweep);
})();

/* ================= jp-video-poster-alt ================= */
/* Shopify's `video_tag` filter emits the poster <img> itself and gives the
   theme no way to set its alt, so it ships without one — the single
   alt-less image left on the homepage. The <video> already carries an
   aria-label describing the clip, so the poster is decorative by definition:
   an empty alt is the correct value, not a missing attribute.
   ponytail: one attribute, no markup change, no dependency on the app that
   generated the tag. */
(function () {
  'use strict';
  function fix() {
    [].slice.call(document.querySelectorAll('video img:not([alt]), .jp-farm-video__frame img:not([alt])'))
      .forEach(function (img) { img.setAttribute('alt', ''); });
  }
  fix();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fix);
  window.addEventListener('load', fix);
})();

/* ================= jp-kin-fallback ================= */
/* Reveals for browsers without scroll-driven animations.
   
   Safari — so every iPhone — does not support `animation-timeline: view()`.
   The stylesheet's @supports block used to switch the motion off entirely for
   those browsers, which is why the site felt alive on a desktop and static on
   a phone. jp-kinetic.css now gives them a transition-based version of the
   same reveal; this adds the class that releases it, the way the Apex site
   does it.
   
   Does nothing at all where view() works: those browsers already animate from
   the stylesheet, and running both would fight.
   
   ponytail: one observer, unobserve on first hit, no scroll listener. */
(function () {
  'use strict';

  if (!window.CSS || CSS.supports('animation-timeline', 'view()')) return;
  if (!window.IntersectionObserver) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var SEEN = 'jp-kin-seen';

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      e.target.classList.add(SEEN);
      io.unobserve(e.target);
    });
    // Fires once the element is genuinely on screen rather than the instant its
    // top edge crosses the fold — the same reason the CSS ranges start at
    // entry 25%: a reveal the reader never sees may as well not exist.
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.15 });

  function claim(root) {
    [].slice.call((root || document).querySelectorAll('[data-kin], [data-kin-group] > *'))
      .forEach(function (el) {
        if (el.__jpKinFb) return;
        el.__jpKinFb = true;
        io.observe(el);
      });
  }

  claim();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { claim(); });
  window.addEventListener('load', function () { claim(); });

  // Sections mount late on this theme, so pick up whatever arrives after.
  if (window.MutationObserver) {
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      setTimeout(function () { queued = false; claim(); }, 120);
    }).observe(document.body, { childList: true, subtree: true });
  }

  // Nothing may stay held back: if an observer callback never lands, release
  // everything rather than leaving the page mid-reveal.
  setTimeout(function () {
    [].slice.call(document.querySelectorAll('[data-kin], [data-kin-group] > *'))
      .forEach(function (el) { el.classList.add(SEEN); });
  }, 6000);
})();

/* ================= jp-words ================= */
/* Per-word reveal for headings and lead copy.
   
   The benchmark splits a lead paragraph into one span per word, each with a
   small transition-delay, so the sentence writes itself in. Every reveal on
   this site was block-level before this: a section arrived, but the paragraph
   inside it landed as one slab, which is why the text kept reading static no
   matter how many kinds of block entrance existed.
   
   Scope is deliberate. Headings and the lead paragraph of a section only —
   staggering a whole page of body copy is noise, and a long paragraph would
   still be finishing after the reader has moved on.
   
   Safety: only elements whose children are plain text get split. A heading
   containing a link, a <br>, or any markup is left exactly as it is, because
   rebuilding its innerHTML would drop event listeners and break the markup. */
(function () {
  'use strict';

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!window.IntersectionObserver) return;

  // Body copy is in scope now, not just headings, so the ceiling has to hold a
  // real paragraph. A 60-word paragraph at 26ms a word would take 1.5s to
  // finish, which is longer than a reader waits — the per-word delay drops to
  // 12ms in the CSS so a long paragraph still lands inside ~0.7s.
  var MAX_WORDS = 70;
  var MIN_WORDS = 2;

  function splittable(el) {
    if (el.__jpWords) return false;
    if (el.closest('header, footer, nav, .jp-bn, .jp-offer, .jp-wa-wrap')) return false;
    // Never a card's own title. Card titles carry data-kin="head" like any
    // other heading, so excluding the h3 selector did nothing — the element
    // still matched through that attribute. Splitting one grows the line box
    // (each word is an inline-block) and inside a card laid out to a fixed
    // height it pushed the title 17px down over the price.
    // Matched on `[class*="card"]` at first, which was far too wide: it is a
    // substring test, so a SECTION named jp-fssai-testing-cards excluded its
    // own heading and lead paragraph, and the quality page measured 76% alive
    // with its two proof sections completely inert. The hazard is a title
    // inside a card box, not the word "card" anywhere in an ancestor's class,
    // so the guard names the card element itself and the title within it.
    if (el.closest('.product-card, .jp-product-card, [class*="__card"], [class*="-card"]') &&
        el.closest('[class*="title"], [class*="name"], h3, h4')) return false;
    if (el.closest('[class*="goes-well"]')) return false;
    // Plain text only: no element children at all.
    if (el.children.length) return false;
    var txt = el.textContent.trim();
    if (!txt) return false;
    var n = txt.split(/\s+/).length;
    return n >= MIN_WORDS && n <= MAX_WORDS;
  }

  function split(el) {
    var words = el.textContent.trim().split(/\s+/);
    el.__jpWords = true;
    var frag = document.createDocumentFragment();
    words.forEach(function (w, i) {
      var s = document.createElement('span');
      s.className = 'jp-w';
      s.style.setProperty('--w', i);
      s.textContent = w;
      frag.appendChild(s);
      // A real space between spans, so selection and wrapping behave normally.
      if (i < words.length - 1) frag.appendChild(document.createTextNode(' '));
    });
    el.textContent = '';
    el.appendChild(frag);
    return true;
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      e.target.classList.add('jp-words-in');
      io.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.2 });

  // Elements split and still waiting for their entrance. The rescue below
  // walks these rather than every .jp-w span.
  var held = [];

  function sweep() {
    // Section headings and ledes only — never a card title.
    //
    // h3 used to be in here, and product cards title themselves with h3. Each
    // word becomes an inline-block, which grows the line box; inside a card
    // laid out to a fixed height that pushed the title down over the price by
    // 17px. A card title is one short line and gains nothing from a per-word
    // cascade anyway.
    var sel = '.shopify-section h1, .shopify-section h2, '
            + '.shopify-section p, .shopify-section li, '
            + '[data-kin="lede"], [data-kin="head"]';
    [].slice.call(document.querySelectorAll(sel)).forEach(function (el) {
      if (!splittable(el)) return;
      if (!split(el)) return;
      // The fade is opted into here, never in the stylesheet. Holding opacity
      // in CSS left anything the observer never reached stranded at .06 —
      // measured on /pages/bilona-method, entire paragraphs were unreadable.
      // Marking it only once this element is genuinely under observation means
      // the class and the thing that clears it always ship together.
      el.classList.add('jp-w-fade');
      held.push(el);
      io.observe(el);
    });
  }

  sweep();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sweep);
  window.addEventListener('load', sweep);

  if (window.MutationObserver) {
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      setTimeout(function () { queued = false; sweep(); }, 150);
    }).observe(document.body, { childList: true, subtree: true });
  }

  // Nothing stays held back if a callback never lands — but this used to
  // release the WHOLE page at 6s, including everything still far below the
  // fold. Measured on the homepage: a paragraph at docTop 8782 was already
  // `jp-words-in` with every word at opacity 1 while parked 1500px above it,
  // so scrolling to it revealed nothing. That is exactly the "text is still
  // not dynamic" reading — the words were split, then pre-opened.
  //
  // Release only what is actually on screen, and keep checking, so a stalled
  // observer still cannot leave a visible paragraph half-drawn while text the
  // reader has not reached stays held for its own entrance.
  // Walk the split ELEMENTS, not every word. Querying .jp-w meant iterating
  // 663 spans on the homepage and measuring each one's parent — measured, a
  // scroll cost 497-614ms of style recalculation across 254-374 recalcs, the
  // single largest cost on the page. The hosts are known here already, so the
  // rescue reads at most one rect per paragraph and drops each one as soon as
  // it has been released.
  function rescue() {
    if (!held.length) return;
    var vh = window.innerHeight;
    held = held.filter(function (host) {
      if (host.classList.contains('jp-words-in')) return false;
      var r = host.getBoundingClientRect();
      if (r.bottom > 0 && r.top < vh) { host.classList.add('jp-words-in'); return false; }
      return true;
    });
  }
  setTimeout(rescue, 6000);
  window.addEventListener('scroll', function () {
    if (rescue.__q) return;
    rescue.__q = true;
    setTimeout(function () { rescue.__q = false; rescue(); }, 400);
  }, { passive: true });
})();

/* ================= jp-stagger ================= */
/* Lists whose items arrive one at a time.
   A container opts in by carrying data-jp-stagger; its children space
   themselves by --i in that section's own CSS, and this only decides WHEN the
   run starts. Same shape as jp-bilona-run — one class toggle, no per-item
   observer — because every item in a short list shares one entry moment and a
   range-based reveal would fire them together. */
(function () {
  'use strict';
  if (!window.IntersectionObserver) return;

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-in');
      io.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.15 });

  // [data-jp-reveal] is observed here too. It used to be choreographed by
  // jp-system.js, which no longer loads at all — so those elements were
  // orphaned: nothing armed the CSS gate, nothing ever added .is-in, and they
  // simply rendered. Measured on /pages/quality: 32 of the 42 reveals below
  // the fold were already at full opacity before any scrolling.
  var SEL = '[data-jp-stagger],[data-jp-reveal]';
  // .jp-reveal is released by jp-motion.js adding .jp-in, so it is not in SEL
  // — but its opacity hold is gated on the same jp-reveal-live class set
  // below, which means this block owns when that hold expires for it too.

  // Arming is what turns the hold on. It happens here, immediately before the
  // first observe, so the content is only ever hidden while something is
  // actually watching it.
  document.documentElement.classList.add('jp-reveal-armed', 'jp-reveal-live');

  function sweep() {
    [].slice.call(document.querySelectorAll(SEL)).forEach(function (el) {
      if (el.__jpStag) return;
      el.__jpStag = true;
      io.observe(el);
    });
  }

  sweep();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sweep);
  window.addEventListener('load', sweep);

  // The safety net, because this file has blanked content four times.
  //
  // It cannot be a short global timer: dropping the hold after a couple of
  // seconds would mean everything below the fold arrives already visible,
  // which is the bug being fixed. Instead each element is released on its own
  // once it has been under observation long enough that a missed callback is
  // the likelier explanation than "not scrolled to yet".
  //
  // ponytail: one interval over the held set, not a timer per element.
  var GRACE = 8000;
  var t0 = Date.now();
  var net = setInterval(function () {
    var held = document.querySelectorAll(
      SEL.replace(/,/g, ':not(.is-in),') + ':not(.is-in),.jp-reveal:not(.jp-in)');
    if (!held.length) { clearInterval(net); return; }
    var vh = innerHeight;
    [].slice.call(held).forEach(function (el) {
      var r = el.getBoundingClientRect();
      // On screen and still held means the observer did not deliver for it.
      if (r.bottom > 0 && r.top < vh && Date.now() - t0 > 1200) {
        // .jp-reveal is jp-motion.js's vocabulary; the rest are this file's.
        el.classList.add(el.classList.contains('jp-reveal') ? 'jp-in' : 'is-in');
      }
    });
    // Whatever is left after the grace period stops being hidden, but keeps
    // its own arrival: removing the live class only lifts the opacity hold.
    if (Date.now() - t0 > GRACE) {
      document.documentElement.classList.remove('jp-reveal-live');
      clearInterval(net);
    }
  }, 400);
})();

/* ================= jp-table-life ================= */
/* Marks tables and their rows as seen, so the CSS can land its marks and draw
   its rules in sequence as the table scrolls in.

   The comparison read as something to get through rather than something to
   use: every row weighed the same and nothing answered the pointer. The
   styling lives in the section; this only supplies the trigger, and only for
   real comparison tables — a table with one row has nothing to compare. */
(function () {
  'use strict';
  if (!window.IntersectionObserver) return;

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-seen');
      io.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.15 });

  function sweep() {
    [].slice.call(document.querySelectorAll('.jp-wct')).forEach(function (t) {
      if (t.__jpTbl) return;
      var rows = t.querySelectorAll('.jp-wct__row');
      if (rows.length < 2) return;
      t.__jpTbl = true;
      io.observe(t);
      [].slice.call(rows).forEach(function (r, i) {
        r.style.setProperty('--i', i);
        io.observe(r);
      });
    });
  }

  sweep();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sweep);
  window.addEventListener('load', sweep);
})();

/* ================= jp-lazy ================= */
/* Images far below the fold are fetched lazily.

   Measured: 9 on the homepage and 10 on a product page sat well past the fold
   with loading="eager", so they competed for bandwidth with everything the
   reader could actually see.

   Only images starting more than 1.5 viewports down are touched — anything
   nearer may be reached before a lazy fetch completes, and the cinema's own
   artwork is left alone entirely because it is revealed by the scrub and was
   already broken once by lazy-loading. */
(function () {
  'use strict';
  function sweep() {
    [].slice.call(document.images).forEach(function (img) {
      if (img.loading === 'lazy' || img.__jpLazy) return;
      if (img.closest('.jp-cin, [class*="cinema"]')) return;
      var r = img.getBoundingClientRect();
      if (r.top + window.scrollY < window.innerHeight * 1.5) return;
      img.__jpLazy = true;
      img.loading = 'lazy';
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  } else { sweep(); }
})();

/* ================= jp-idle-anim ================= */
/* Infinite animations pause while they are off screen.

   Measured on the homepage: 18 elements carried an infinite CSS animation and
   13 of them were outside the viewport, so the compositor was kept awake all
   the way down the page for motion nobody could see — 12 long tasks and a
   226ms worst frame gap during a scroll.

   Pausing is animation-play-state only: the keyframes, timing and current
   position are untouched, so a marquee resumes exactly where it left off
   rather than restarting. ponytail: one observer for the whole page, not a
   per-section opt-in — anything that animates forever qualifies. */
(function () {
  'use strict';
  if (!window.IntersectionObserver) return;

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      e.target.style.animationPlayState = e.isIntersecting ? '' : 'paused';
    });
    // A margin, so something re-entering is already running by the time it is
    // actually on screen and never shows a stalled first frame.
  }, { rootMargin: '200px 0px 200px 0px' });

  function sweep() {
    [].slice.call(document.querySelectorAll('body *')).forEach(function (el) {
      if (el.__jpIdle) return;
      var cs = getComputedStyle(el);
      if (cs.animationName === 'none' || cs.animationIterationCount !== 'infinite') return;
      el.__jpIdle = true;
      io.observe(el);
    });
  }

  // After first paint, and again once late sections have rendered. Reading
  // computed style for every node is not free, so this runs a handful of
  // times rather than on a MutationObserver.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(sweep, 400); });
  } else {
    setTimeout(sweep, 400);
  }
  window.addEventListener('load', function () { setTimeout(sweep, 1200); });
})();

/* ================= jp-count ================= */
/* Figures count up to their value as they arrive.

   A statistic that simply fades in is a printed number; one that counts is the
   page telling you it matters. The classifier in jp-kinetic already marks
   these as [data-kin="stat"], so this only has to animate the ones it found.

   Handles the shapes actually on this site — "4g", "15-20L", "1,500+", "138" —
   by counting only the digit runs and leaving every other character in place,
   so a range counts both of its ends and a suffix never flickers. */
(function () {
  'use strict';
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!window.IntersectionObserver) return;

  var DUR = 1100;

  function ease(t) { return 1 - Math.pow(1 - t, 3); }   // out-cubic, matches the site's curve

  function run(el) {
    if (el.__jpCount) return;
    var text = el.textContent.trim();
    // Split into digit runs and everything else, so "15-20L" keeps its dash and L.
    var parts = text.split(/(\d[\d,]*)/);
    if (!parts.some(function (p) { return /^\d/.test(p); })) return;
    el.__jpCount = true;

    var targets = parts.map(function (p) {
      if (!/^\d/.test(p)) return null;
      return { raw: p, value: parseInt(p.replace(/,/g, ''), 10), grouped: p.indexOf(',') > -1 };
    });

    var started = null;
    function frame(now) {
      if (started === null) started = now;
      var t = Math.min(1, (now - started) / DUR);
      var e = ease(t);
      el.textContent = parts.map(function (p, i) {
        var tg = targets[i];
        if (!tg) return p;
        var n = Math.round(tg.value * e);
        return tg.grouped ? n.toLocaleString('en-IN') : String(n);
      }).join('');
      if (t < 1) requestAnimationFrame(frame);
      else el.textContent = text;    // land exactly on the authored string
    }
    requestAnimationFrame(frame);
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      io.unobserve(e.target);
      run(e.target);
    });
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.4 });

  function sweep() {
    [].slice.call(document.querySelectorAll('[data-kin="stat"]')).forEach(function (el) {
      if (el.__jpCountSeen) return;
      el.__jpCountSeen = true;
      io.observe(el);
    });
  }

  sweep();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sweep);
  window.addEventListener('load', sweep);
  // The classifier tags stats after its own sweep, so look again once it has run.
  setTimeout(sweep, 1200);
  setTimeout(sweep, 3000);
})();

/* ================= jp-bilona-run ================= */
/* The seven bilona steps arrive one at a time.

   They used to arrive together on desktop: the section's scroll-driven block
   was written inside a max-width:749px media query, so only phones got the
   sequence. Every larger screen showed all seven at once in a single row —
   which is what the owner kept calling flat.

   A view() timeline cannot fix that on its own. Steps sitting in one row all
   cross the viewport edge in the same frame, so they share one entry moment
   and a range-based reveal fires them simultaneously. The order has to come
   from each step's own index instead: the CSS holds every step at its start
   state and staggers the transition by --i, and this only has to say WHEN the
   run begins.

   ponytail: one class toggle, no per-step observers, no scroll listener. */
(function () {
  'use strict';
  if (!window.IntersectionObserver) return;

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-run');
      io.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -18% 0px', threshold: 0.15 });

  function sweep() {
    [].slice.call(document.querySelectorAll('.jp-bilona-timeline')).forEach(function (el) {
      if (el.__jpRun) return;
      el.__jpRun = true;
      io.observe(el);
    });
  }

  sweep();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sweep);
  window.addEventListener('load', sweep);
})();
